<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import type { WikiPageRelation } from '../stores/wiki_pages'
import { RELATION_KINDS, relationKindLabel, type WikiPageRelationKind } from '../composables/relationKind'

// Relations panel rendered between the WikiPageMeta bar and the
// article body on the wiki page view. Surfaces the typed-edge graph
// (supersedes / uses / contradicts / part-of / see-also) plus the
// deterministic `mentioned-by` backlinks the BacklinkReconciler keeps
// in sync.
//
// This panel is the SOLE UI surface for relations on a wiki page —
// `HomeView` strips the bottom-of-page `## Relations` markdown via
// `composables/stripRelationsBlock` before rendering the article body
// Disk content is untouched; the strip is a render-time
// concern only. If a future change adds another rendering of the
// relations data, fold this panel into that surface rather than
// reintroducing the duplication.
//
// The canonical kind order and the human labels live in
// `composables/relationKind.ts`, the single TS-side source of truth.
// This component owns only the grouping + within-group ordering.
// Within each group we sort by `to_title` so the panel renders
// alphabetically regardless of the server's emission order — the
// serializer happens to pre-sort today, but the panel does not depend
// on that contract.
//
// `to_id` is null when the target page hasn't been authored yet
// (legitimate per WikiPageRelation's soft FK); we render those rows as
// muted text instead of a router link.
const props = defineProps<{
  relations: WikiPageRelation[]
}>()

interface RelationGroup {
  kind: WikiPageRelationKind
  label: string
  entries: WikiPageRelation[]
  total: number
}

// A well-connected page can carry 30+ `mentioned-by` backlinks, which
// would make the panel taller than the article excerpt. Cap each group
// and offer a per-group "Show all" disclosure. Typed-edge groups
// (supersedes/contradicts/…) are usually 1–3 entries, so the cap only
// ever bites mentioned-by in practice.
const GROUP_CAP = 5
const expandedKinds = ref<Set<WikiPageRelationKind>>(new Set())

const allGroups = computed(() => {
  const byKind = new Map<WikiPageRelationKind, WikiPageRelation[]>()
  for (const rel of props.relations) {
    const bucket = byKind.get(rel.kind)
    if (bucket) bucket.push(rel)
    else byKind.set(rel.kind, [rel])
  }
  return RELATION_KINDS.filter((kind) => byKind.has(kind)).map((kind) => ({
    kind,
    entries: byKind.get(kind)!.toSorted((a, b) => a.to_title.localeCompare(b.to_title)),
  }))
})

const groups = computed<RelationGroup[]>(() =>
  allGroups.value.map(({ kind, entries }) => {
    const expanded = expandedKinds.value.has(kind) || entries.length <= GROUP_CAP
    return {
      kind,
      label: relationKindLabel(kind),
      entries: expanded ? entries : entries.slice(0, GROUP_CAP),
      total: entries.length,
    }
  }),
)

function toggleKind(kind: WikiPageRelationKind): void {
  const next = new Set(expandedKinds.value)
  if (next.has(kind)) next.delete(kind)
  else next.add(kind)
  expandedKinds.value = next
}
</script>

<template>
  <section
    v-if="groups.length > 0"
    class="mb-4 border border-white/10 rounded px-3 py-2 text-sm"
    data-testid="relations-panel"
    aria-label="Page relations"
  >
    <h2 class="sr-only">Relations</h2>
    <!-- One row per group: kind label on the left, entries stacked on
         the right. Stacked (not inline + `·` separators) so the
         per-entry context phrase has room to render
         without making the row hard to scan — and so a single layout
         works whether the group has one entry or ten. -->
    <dl class="m-0 flex flex-col gap-1">
      <div
        v-for="group in groups"
        :key="group.kind"
        class="flex items-baseline gap-x-2"
        :data-testid="`relations-group-${group.kind}`"
      >
        <dt class="text-white/40 shrink-0 min-w-[7.5rem]">{{ group.label }}</dt>
        <dd :id="`relations-dd-${group.kind}`" class="m-0 flex-1 min-w-0 flex flex-col gap-y-0.5">
          <div v-for="entry in group.entries" :key="`${entry.kind}:${entry.to_path}`" data-testid="relation-entry">
            <RouterLink
              v-if="entry.to_id !== null"
              :to="`/p/${entry.to_path}`"
              class="text-brand-primary hover:text-white no-underline"
              data-testid="relation-link"
              >{{ entry.to_title }}</RouterLink
            >
            <span v-else class="text-white/50" :title="entry.to_path" data-testid="relation-missing">{{
              entry.to_title
            }}</span>
            <span v-if="entry.context" class="text-white/40" data-testid="relation-context">
              — {{ entry.context }}</span
            >
          </div>
          <button
            v-if="group.total > group.entries.length"
            type="button"
            class="self-start text-xs text-brand-primary hover:underline mt-0.5"
            data-testid="relations-show-all"
            :aria-expanded="expandedKinds.has(group.kind)"
            :aria-controls="`relations-dd-${group.kind}`"
            @click="toggleKind(group.kind)"
          >
            Show all {{ group.total }}
          </button>
          <button
            v-else-if="expandedKinds.has(group.kind) && group.total > GROUP_CAP"
            type="button"
            class="self-start text-xs text-white/40 hover:text-white mt-0.5"
            data-testid="relations-show-less"
            :aria-expanded="expandedKinds.has(group.kind)"
            :aria-controls="`relations-dd-${group.kind}`"
            @click="toggleKind(group.kind)"
          >
            Show less
          </button>
        </dd>
      </div>
    </dl>
  </section>
</template>
